import type { SRT_RowSelectionState } from '@/shared/components/master-table'
import type { PricingCampaignModel } from '@/shared/generated'
import type { PricingCampaignTableRow } from '@/widgets/scenario-pricing-campaigns'

import React, { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
	entries,
	find,
	fromPairs,
	indexBy,
	map,
	pipe,
	prop,
	propEq,
	propOr,
	sum,
	values,
} from 'lodash/fp'
import { ArrowDownUpIcon, PlusIcon, SearchIcon, Trash2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useOptimizationQuery } from '@/entities/optimization'
import {
	alignPricingCampaignsOrders,
	getAssortmentShare,
	getPreOrderedCampaigns,
	isGlobalPricingCampaign,
	isValidMatchedProductsRequest,
	isValidMatchedProductsRequestItem,
	useBulkDeletePricingCampaignsMutation,
	useMatchedProductsQuery,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'
import { useScenarioQuery } from '@/entities/scenario'

import {
	getPricingCampaignsTableRows,
	useScenarioPricingCampaignsTable,
} from '@/widgets/scenario-pricing-campaigns'

import { MasterTable, SRT_TableToolbar } from '@/shared/components/master-table'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { DuplicateScenarioPricingCampaignModal } from './DuplicateScenarioPricingCampaignModal'

export const ScenarioPricingCampaigns = ({
	scenarioId,
}: {
	scenarioId: string
}) => {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const { showModal } = useModals()
	const [rowSelection, setRowSelection] = useState<SRT_RowSelectionState>({})
	const scenarioQuery = useScenarioQuery(scenarioId, {
		enabled: !!scenarioId,
	})
	const scenario = scenarioQuery.data
	const optimization_group_id = scenario?.optimization_group_id
	const campaigns = usePricingCampaignsQuery<PricingCampaignModel[]>(
		{
			optimization_group_id,
			scenario_id: scenarioId,
		},
		{
			enabled: !!optimization_group_id,
			select: getPreOrderedCampaigns,
		}
	)
	const totalProducts = useOptimizationQuery<number>(optimization_group_id!, {
		enabled: !!optimization_group_id,
		select: propOr(0, 'total_products'),
	})
	const assignments = useMemo(
		() =>
			alignPricingCampaignsOrders(campaigns.data || [])
				?.map((pc) => ({
					id: pc.id,
					order: pc.order,
					assignments:
						pc.settings.product_assignments?.map(({ name, filters }) => ({
							id: name,
							filters,
						})) || [],
				}))
				.filter(isValidMatchedProductsRequestItem) || [],
		[campaigns.data]
	)
	const predictedCounts = useMatchedProductsQuery<Record<string, number>>(
		{
			scenario_id: scenarioId ?? null,
			pricing_campaigns: assignments,
		},
		{
			select: pipe([
				indexBy(prop('id')),
				entries,
				map(([id, { pricing_line_count }]) => [id, pricing_line_count]),
				fromPairs,
			]),
			enabled:
				isValidMatchedProductsRequest(assignments) && !!optimization_group_id,
		}
	)
	const unassignedCount =
		(totalProducts.data ?? 0) - (sum(values(predictedCounts.data ?? {})) ?? 0)
	const bulkDeletePricingCampaigns = useBulkDeletePricingCampaignsMutation()
	const selected = useMemo(() => Object.keys(rowSelection), [rowSelection])
	const handleDeleteSelectedPricingCampaigns = useCallback(async () => {
		ampli.scenariosPricingCampaignsDelete({
			count: selected.length,
		})
		const answer = await confirm({
			title: t('pc_detete_confirm_title'),
			description: t('pc_detete_confirm_subtitle'),
			actionText: t('general_delete', {
				ns: 'keys',
			}),
			cancelText: t('general_cancel', {
				ns: 'keys',
			}),
			ActionProps: {
				variant: 'destructive',
			},
		})
		if (answer) {
			bulkDeletePricingCampaigns.mutate(selected, {
				onSettled: () => {
					queryClient.invalidateQueries({
						queryKey: ['pricing-campaigns'],
					})
					queryClient.invalidateQueries({
						queryKey: ['pricing-campaigns-overview'],
					})
					queryClient.invalidateQueries({
						queryKey: ['pricing-campaigns-segments'],
					})
					setRowSelection({})
				},
			})
		}
	}, [selected, bulkDeletePricingCampaigns, queryClient, t])
	const data: PricingCampaignTableRow[] = useMemo(
		() =>
			getPricingCampaignsTableRows(
				campaigns.data || [],
				predictedCounts.data || {},
				totalProducts.data
			),
		[campaigns.data, predictedCounts.data, totalProducts.data]
	)
	const isEnabledDeleting = useMemo(
		() =>
			selected.length > 0 &&
			selected.every((id) => {
				const pc = find(propEq('campaign.id', id), data)

				return pc?.campaign && !isGlobalPricingCampaign(pc.campaign)
			}),
		[selected, data]
	)
	const table = useScenarioPricingCampaignsTable({
		scenario,
		isLoading: scenarioQuery.isLoading || campaigns.isLoading,
		rowSelection,
		setRowSelection,
		data,
	})

	return (
		<Card className='overflow-visible shadow-none'>
			<CardHeader className='flex flex-row items-center justify-between'>
				<CardTitle>
					{t('general_pc', { defaultValue: 'Pricing campaigns' })}
				</CardTitle>
			</CardHeader>
			<CardContent className='h-96 flex-col overflow-hidden'>
				{!data.length && !campaigns.isLoading ? (
					<div className='flex size-full flex-col items-center justify-center space-y-2 bg-secondary text-sm'>
						<SearchIcon height={72} width={72} />
						<p className='font-medium text-muted-foreground'>
							{t('scenario.empty_pcs.title')}
						</p>
					</div>
				) : (
					<div className='flex h-full flex-col gap-2'>
						<div
							role='toolbar'
							aria-orientation='horizontal'
							className='flex w-full items-start justify-between gap-2'
						>
							<SRT_TableToolbar table={table} />
							<div className='flex items-center gap-2'>
								{!!unassignedCount && (
									<Badge variant='warning'>
										{`${t('general_unassigned')}: ${formatNumber(unassignedCount)} (${formatNumber(
											getAssortmentShare(
												totalProducts.data || 0,
												unassignedCount
											),
											NumberFormats.Percent
										)})`}
									</Badge>
								)}
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											disabled={!isEnabledDeleting || scenario?.is_main}
											size='icon'
											onClick={handleDeleteSelectedPricingCampaigns}
										>
											<Trash2Icon />
										</Button>
									</TooltipTrigger>
									<TooltipContent>
										{t('scenario.delete_pcs', {
											defaultValue: 'Delete selected PC',
										})}
									</TooltipContent>
								</Tooltip>
								<Tooltip>
									<TooltipTrigger asChild>
										<Button
											size='icon'
											disabled={scenario?.is_main}
											onClick={() => {
												ampli.scenariosPricingCampaignsReorder()
												showModal('PRICING_CAMPAIGNS_PRIORITY', {
													scenarioId: scenarioId,
													optimizationGroupId: optimization_group_id,
												})
											}}
										>
											<ArrowDownUpIcon />
										</Button>
									</TooltipTrigger>
									<TooltipContent>
										{t('scenario.change_priority', {
											defaultValue: 'Change priority',
										})}
									</TooltipContent>
								</Tooltip>
								{!!optimization_group_id && (
									<DuplicateScenarioPricingCampaignModal
										optimizationGroupId={optimization_group_id}
										toScenarioId={scenarioId}
										toScenarioName={t('entity.scenario', {
											defaultValue: 'Scenario',
										})}
									>
										<Button
											size='icon'
											disabled={scenario?.is_main}
											onClick={() => {
												ampli.scenariosDuplicatePricingCampaigns({
													location: 'toolbar',
												})
											}}
										>
											<PlusIcon />
										</Button>
									</DuplicateScenarioPricingCampaignModal>
								)}
							</div>
						</div>
						<div className='border-base relative flex h-full flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
							<MasterTable table={table} />
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	)
}
